import {
  ComponentProps,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";

import { TableContainerBox } from "./AdminPage.styles";
import { ITableCsvExport } from "./tableCsv";
import { TableExportContext } from "./TableExportContext";

interface ITableContainerProps
  extends ComponentProps<typeof TableContainerBox> {
  children: ReactNode;
}

/** Same object, field for field — pages rebuild the config every render. */
function sameExport(
  a: ITableCsvExport<unknown> | null,
  b: ITableCsvExport<unknown> | null
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.filename === b.filename &&
    a.columns === b.columns &&
    a.rows === b.rows &&
    a.currentPageOnly === b.currentPageOnly
  );
}

/**
 * The card an admin table and its pagination footer sit in. This used to be a
 * bare styled div (now `TableContainerBox`); as a component it can carry the
 * `TableExportContext` between the footer, which knows the CSV export, and
 * the header, which now shows it — so every existing page gets the header
 * export icon with no edits.
 */
export function TableContainer({ children, ...rest }: ITableContainerProps) {
  const [config, setConfig] = useState<ITableCsvExport<unknown> | null>(null);

  const publish = useCallback((next: ITableCsvExport<unknown> | null) => {
    setConfig((prev) => (sameExport(prev, next) ? prev : next));
  }, []);

  const value = useMemo(() => ({ config, publish }), [config, publish]);

  return (
    <TableExportContext.Provider value={value}>
      <TableContainerBox {...rest}>{children}</TableContainerBox>
    </TableExportContext.Provider>
  );
}
